/**
 * Read-only itinerary view (`/itineraries/:slug`). Renders the full day/stop
 * structure with the travel-time pill and dangling-stop placeholder (D4),
 * plus the archived banner (spec "Archived itinerary banner", warm-gold — not
 * error crimson). The owner gets an Edit affordance; the editor is the
 * separate `/edit` route. No in-page "Back to…" links (repo rule #154).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ItineraryWithViewFlags } from './itineraryTypes';
import { ItineraryDayPanel } from './ItineraryDayPanel';
import { ItineraryDayTabs, dayPanelId, dayTabId } from './ItineraryDayTabs';
import { ItineraryReviewsSection } from './ItineraryReviewsSection';
import { PageContainer } from '../PageContainer';
import { Heading1, BodyText, Caption } from '../ui/Typography';
import { Button } from '../ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { trpc } from '../../lib/trpc';
import { persistCloneIntent } from './useCloneIntent';

interface ItineraryViewProps {
  itinerary: ItineraryWithViewFlags;
}

export const ItineraryView = ({ itinerary: it }: ItineraryViewProps) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const isOwner = Boolean(user && user.id === it.ownerId);
  const archived = it.editorialStatus === 'archived';
  const [staleDismissed, setStaleDismissed] = useState(false);
  const [activeDayId, setActiveDayId] = useState<string | null>(it.days[0]?._id ?? null);

  // Keep active tab valid if days change (e.g., owner edits in another tab).
  useEffect(() => {
    if (activeDayId && it.days.some((d) => d._id === activeDayId)) return;
    setActiveDayId(it.days[0]?._id ?? null);
  }, [it.days, activeDayId]);

  // A viewable public itinerary the viewer does not own can be forked.
  // Archived itineraries are not a clone source.
  const canClone = !isOwner && it.audience === 'public' && !archived;

  const cloneMutation = trpc.itinerary.clone.useMutation({
    onSuccess: (clone) => {
      navigate(`/itineraries/${clone.publicSlug}/edit`);
    },
  });

  const handleUseAsTemplate = () => {
    cloneMutation.mutate({ sourceSlug: it.publicSlug });
  };

  const handleSignInToSave = () => {
    // Persist the intent (sessionStorage + 15-min TTL — see useCloneIntent),
    // then send to the existing sign-in route. On return, the auto-retry hook
    // mounted on the view page fires the clone without a re-click.
    persistCloneIntent(it.publicSlug);
    navigate('/login');
  };

  // Stale-source affordance is shown ONLY when the server says the source is
  // still resolvable + viewable AND changed (it.staleSource). When the source
  // is archived / hard-deleted / no-longer-viewable the server returns
  // staleSource=false, so the prompt is hidden — never a 404.
  const showStalePrompt = it.staleSource && !staleDismissed;

  return (
    <div className="px-0 md:px-4 pt-2 pb-4">
      <PageContainer className="mt-2">
        <div className="border-b border-[#DBD4C9] pb-4">
          {(isOwner || canClone) && (
            <div className="mb-4 flex flex-wrap justify-end gap-3">
              {isOwner && (
                <Button
                  variant="primary"
                  onClick={() => navigate(`/itineraries/${it.publicSlug}/edit`)}
                >
                  Edit
                </Button>
              )}
              {canClone &&
                (isAuthenticated ? (
                  <Button
                    variant="primary"
                    onClick={handleUseAsTemplate}
                    loading={cloneMutation.isPending}
                    disabled={cloneMutation.isPending}
                  >
                    Use as template
                  </Button>
                ) : (
                  <Button variant="primary" onClick={handleSignInToSave}>
                    Sign in to save
                  </Button>
                ))}
            </div>
          )}

          {archived && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <BodyText className="text-gray-800">
                {isOwner
                  ? "This itinerary is archived — it's hidden from browse but your copy is intact."
                  : it.viewerHoldsClone
                    ? 'The original is archived.'
                    : 'This itinerary is archived and no longer maintained.'}
              </BodyText>
            </div>
          )}

          {showStalePrompt && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <BodyText className="text-gray-800">
                The itinerary this was based on has changed since you saved it.
              </BodyText>
              <div className="flex flex-shrink-0 gap-2">
                {it.clonedFromId && (
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/itineraries/${it.publicSlug}/edit`)}
                  >
                    Review changes
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setStaleDismissed(true)}>
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          <Heading1>{it.title}</Heading1>
          {it.description ? (
            <BodyText className="mt-2 max-w-3xl whitespace-pre-wrap text-gray-700">
              {it.description}
            </BodyText>
          ) : null}
          <Caption className="mt-2 block text-gray-600">
            {it.days.length} {it.days.length === 1 ? 'day' : 'days'} ·{' '}
            {it.days.reduce((n, d) => n + d.stops.length, 0)} stops
            {it.reviewCount > 0 && (
              <>
                {' · '}
                <span aria-hidden="true" className="text-primary-700">
                  ★
                </span>{' '}
                {it.averageRating.toFixed(1)} ({it.reviewCount})
              </>
            )}
          </Caption>
        </div>

        <div className="mt-4">
          <ItineraryDayTabs days={it.days} activeDayId={activeDayId} onSelect={setActiveDayId} />

          {it.days.map((day, di) => {
            if (day._id !== activeDayId) return null;
            return (
              <div
                key={day._id}
                role="tabpanel"
                id={dayPanelId(day._id)}
                aria-labelledby={dayTabId(day._id)}
                className="mt-3"
              >
                <ItineraryDayPanel
                  day={day}
                  dayIndex={di}
                  dayCount={it.days.length}
                  readOnly
                  grab={null}
                  onSetGrab={() => undefined}
                  onMove={() => undefined}
                  onDropStop={() => undefined}
                  onAddVenue={() => undefined}
                  onAddPlace={() => undefined}
                  onEditDayTitle={() => undefined}
                  onRemoveDay={() => undefined}
                  onEditStopDetails={() => undefined}
                  onRemoveStop={() => undefined}
                />
              </div>
            );
          })}
        </div>

        <ItineraryReviewsSection
          itineraryId={it._id}
          slug={it.publicSlug}
          averageRating={it.averageRating}
          reviewCount={it.reviewCount}
          isOwner={isOwner}
          isReviewableTarget={it.audience === 'public' && it.editorialStatus === 'published'}
        />
      </PageContainer>
    </div>
  );
};
